import { useState, useEffect, useCallback, useReducer } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { SortingState } from '@tanstack/react-table';

import { getProducts } from 'api/generated/product-controller/product-controller';
import { dictionary } from 'api/market-view-api';

import { Stack } from 'ui/stack';
import { Loader } from 'ui/loader';
import { Typography } from 'ui/typography';
import { SearchField } from 'ui/search-field';

import Button from 'components/partials/button/button';
import TableComponent from 'components/partials/table-component/table-component';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import {
  getReportsTableSettings,
  type ReportRow,
  type ProductOption,
} from 'containers/admin/reports/reports-table-settings';

import { useCurrentUser } from 'hooks/api/use-current-user';
import { useAdminReportsList } from 'hooks/api/use-admin-reports';

import { usersReducer } from 'containers/admin/reducer';
import { SEARCH_CHANGE, PAGE_CHANGE } from 'containers/admin/actions';

import Icons from 'assets/icons';

interface ReportsAdminPageProps {
  clientId?: string | number;
}

interface ReducerState {
  search: string;
  pageIndex: number;
}

interface DictionaryEntry {
  name?: string;
  description?: string;
}

const initialState: ReducerState = {
  search: '',
  pageIndex: 0,
};

interface TableAmountState {
  pageSize: number;
  totalRecords: number;
}

const ReportsAdminPage = ({
  clientId,
}: ReportsAdminPageProps): React.JSX.Element => {
  const [products, setProducts] = useState<ProductOption[]>([]);
  // productDictionary is fetched but not used by the table; kept for parity
  // with the original JS to preserve runtime behavior (request side effects).
  const [, setProductDictionary] = useState<DictionaryEntry[]>([]);

  const [state, dispatch] = useReducer(usersReducer, initialState);

  const [{ pageSize }, setAmount] = useState<TableAmountState>({
    pageSize: 10,
    totalRecords: 0,
  });

  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: 'title', desc: false },
  ]);

  const handleSearch = useCallback((payload: string): void => {
    dispatch({ type: SEARCH_CHANGE, payload });
  }, []);

  const handlePagination = useCallback((payload: number): void => {
    dispatch({ type: PAGE_CHANGE, payload });
  }, []);

  const currentUser = useCurrentUser();

  const columns = getReportsTableSettings(products);

  const { search, pageIndex } = state as ReducerState;

  const {
    data: reportsResponse,
    isFetching,
    isError,
    isSuccess,
  } = useAdminReportsList({
    pageSize,
    pageIndex,
    sortBy: sortingParams,
    filters: {
      clientId,
      title: { contains: search },
    },
  });

  const data = (reportsResponse?.data ?? []) as ReportRow[];
  const totalRecords = reportsResponse?.pagination?.totalRecords ?? 0;

  useEffect(() => {
    const getProductsDictionary = async (): Promise<void> => {
      try {
        const response = await dictionary({
          userId: currentUser.data?.oktaUserId ?? '',
          type: 'PRODUCT_TYPE',
        });

        if (response?.data) {
          setProductDictionary(response.data);
        }
      } catch (e) {
        console.error('Error while fetching products dictionary', e);
      }
    };

    getProductsDictionary();
  }, []);

  useEffect(() => {
    const fetchProducts = async (): Promise<void> => {
      const userId = currentUser.data?.oktaUserId ?? '';

      if (!userId) return;

      try {
        const response = await getProducts(userId, {
          size: 1000,
          sort: ['name,asc'],
        });

        if (response?.data) {
          setProducts(response.data as ProductOption[]);
        }
      } catch (e) {
        console.error('Error while fetching products', e);
      }
    };

    fetchProducts();
  }, [currentUser.data?.oktaUserId]);

  const renderContent = (): React.JSX.Element | null => {
    if ((!isError && data.length > 0) || (isFetching && isSuccess)) {
      return (
        <TableComponent
          columns={columns}
          data={data}
          pageIndex={pageIndex}
          isLoading={isFetching && isSuccess}
          pageSize={pageSize}
          setPagination={handlePagination}
          setAmount={setAmount}
          sortingParams={sortingParams}
          setSortingParams={setSortingParams}
          totalRecords={totalRecords}
          customHeight="max-h-[50vh]"
        />
      );
    }

    if (!isFetching && data.length === 0) {
      return (
        <EmptySpaceContent
          customClass="flex-1"
          text="There are no reports to show yet."
        />
      );
    }

    return null;
  };

  return (
    <ResponsiveWrapper customClass="flex flex-col">
      <>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            my: 2.5,
          }}
        >
          <Loader loading={isFetching && !isSuccess} />

          <Typography data-testid="admin-reports-heading" variant="h1">
            Reports
          </Typography>
          <RouterLink to="/admin/reports/new" data-testid="add-report-button">
            <Button
              variant="brand-filled"
              size="lg"
              iconBefore={<Icons.Plus aria-label="PlusIcon" />}
            >
              Add a Report
            </Button>
          </RouterLink>
        </Stack>

        <SearchField onSearch={handleSearch} sx={{ mb: 2.5 }} />

        <Stack sx={{ position: 'relative', flex: 1 }}>{renderContent()}</Stack>
      </>
    </ResponsiveWrapper>
  );
};

export default ReportsAdminPage;
